import React, { useState, useRef, useEffect } from "react";
import { ProgressionTemplate } from "../../libs/progression/types";
import { ProgressionWeeksPreview } from "./ProgressionWeeksPreview";

type Props = {
  template: ProgressionTemplate;
  color: string;
  onClick: () => void;
  onClone: () => void;
  onDelete: () => void;
  isCloning: boolean;
};

export const ProgressionListRow: React.FC<Props> = ({
  template,
  color,
  onClick,
  onClone,
  onDelete,
  isCloning,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData("application/fitshape-progression", JSON.stringify(template));
    e.dataTransfer.effectAllowed = "move";
  };

  return (
    <div
      className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer p-5 flex items-start gap-4"
      onClick={onClick}
      draggable
      onDragStart={handleDragStart}
    >
      {/* Left: icon + name */}
      <div className="flex items-center gap-3 flex-shrink-0 w-48">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}18` }}
        >
          <svg className="w-5 h-5" style={{ color }} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
            {template.name}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {template.numWeeks} settimane
          </p>
        </div>
      </div>

      {/* Center: weeks preview */}
      <div className="flex-1 min-w-0">
        <ProgressionWeeksPreview weeks={template.weeks} />
      </div>

      {/* Right: menu ⋮ */}
      <div className="relative flex-shrink-0" ref={menuRef}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 4 20">
            <circle cx="2" cy="2" r="2" />
            <circle cx="2" cy="10" r="2" />
            <circle cx="2" cy="18" r="2" />
          </svg>
        </button>
        {menuOpen && (
          <div
            className="absolute right-0 top-8 z-30 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-600 min-w-[150px] py-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              onClick={() => { setMenuOpen(false); onClone(); }}
              disabled={isCloning}
            >
              Duplica
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              onClick={() => { setMenuOpen(false); onDelete(); }}
            >
              Elimina
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
